import { containers } from '@ecosystem/newron-design-system';
import { ContextManager } from '../../context/manager';
import constants from '../../filters/constants';
import { formatFeatureClassForParams, FormatFeatureClassInput } from '../../filters/utils';
import {
  FilterDefinition,
  FilterSections,
  FilterType,
  ListBoxMenuItem,
  ListMenuItem,
  MenuItemOption,
  Params,
} from '../../types';
import { MenuColumnOption, SecondFeatureSegmentationConfig, SelectedCustomFeatureContext } from './types';
import { TrayType } from '../../types/content/custom.types';
import { PageInfo } from '../../page/types';
import { UiDataParams } from '../../data';
import { FEATURE_SEGMENT, SEGMENT_CUSTOM_FEATURE } from './constants';

const { PRICE_CUSTOMIZATION, FEATURE_CUSTOMIZATION } = constants;
const { FilterTrayTabId } = containers;

/**
 * Function to generate standard item label and custom feature item label
 * @param item selected menu item
 * @param selectedCustomFeature selected custom feature context
 * @returns menu item label
 */
export const generateCustomLabel = (
  item: ListBoxMenuItem,
  selectedCustomFeature?: SelectedCustomFeatureContext
): string => {
  if (!item.isCustom || !selectedCustomFeature || checkIfCustomPriceTier(item)) {
    return item.label;
  }
  const { label, savedName } = selectedCustomFeature;

  if (item.label === 'Custom') {
    item.description = label;
  }

  return `${savedName || label} (Custom)`;
};

/**
 * Function to check if an item is a custom price tier menu item
 * @param item selected menu item
 * @returns true if the value has customPriceTier substring
 */
export const checkIfCustomPriceTier = (item: ListBoxMenuItem) => {
  return (item.value as string).includes('customPriceTier');
};

/**
 * Function to check if the selected feature is applicable to the cell
 * @param baseFeatures list of base features
 * @param selectedCustomFeature selected custom feature context
 * @returns true if the selected custom feature value is in the list of base features
 */
export const checkIsApplicableSavedFeature = (
  baseFeatures: FilterDefinition[],
  selectedCustomFeature: SelectedCustomFeatureContext
): boolean => {
  return baseFeatures.some(feature => feature.name === selectedCustomFeature.value);
};

/**
 * Function to get initial feature class when custom feature menu item is selected
 * @param sections filter sections list
 * @param baseFeatures filter definition list
 * @returns formatted string to pass as customFeatureTiers param for numerical feature and categorical feature
 */
export const getInitialFeatureClass = (sections: FilterSections[], baseFeatures: FilterDefinition[]) => {
  if (!sections || !baseFeatures?.length) return '';

  const firstFeature = baseFeatures[0];

  if (firstFeature.type === FilterType.numericRange) {
    return `name:${firstFeature?.name}|${firstFeature?.min}:`;
  } else {
    const featureClasses = {
      label: 'All',
      selectedOptions: firstFeature.options.map(item => item.value),
      options: firstFeature.options,
    };
    const input: FormatFeatureClassInput = {
      selectedFeatureName: firstFeature.name,
      selectedFeatureType: firstFeature.type,
      segmentClasses: [featureClasses],
      excludeOthers: true,
    };
    return formatFeatureClassForParams(input);
  }
};

/**
 * Function to flatten the first level of menu item to get the length of first level menu item
 * @param items list menu item array
 * @returns flatten list menu item
 */
export function flattenMenuItems(items: ListMenuItem[]): ListMenuItem[] {
  const result: ListMenuItem[] = [];

  for (const item of items) {
    if (
      (item.type === 'MenuItem' && typeof item.value === 'string') ||
      ['MenuItemContext', 'DataColumns'].includes(item.type)
    ) {
      result.push(item);
    } else if (item.type === 'MenuItemGrouped') {
      result.push(...item.list);
    }
  }
  return result;
}

/**
 * Function to handle clicking 'view all' menu item
 * @param item selected menu item
 * @param setStateActions setCustomizationTray, setIsFilterTray, setCustomizationTrayTabId
 * @param selectedCustomFeature selected custom feature context
 * @param paramName parameter name string
 * @param secondSegmentationConfig second segmentation config
 * @param page page information
 */
export const handleViewAllClick = (
  item: ListBoxMenuItem,
  setStateActions: {
    setCustomizationTray: React.Dispatch<React.SetStateAction<TrayType>>;
    setIsFilterTray: React.Dispatch<React.SetStateAction<boolean>>;
    setCustomizationTrayTabId: React.Dispatch<React.SetStateAction<containers.FilterTrayTabId>>;
  },
  selectedCustomFeature: SelectedCustomFeatureContext,
  paramName: string,
  secondSegmentationConfig: SecondFeatureSegmentationConfig,
  page: PageInfo
): void => {
  const { setCustomizationTray, setIsFilterTray, setCustomizationTrayTabId } = setStateActions;
  if (checkIfCustomPriceTier(item)) {
    setCustomizationTray(PRICE_CUSTOMIZATION);
  } else {
    ContextManager.getInstance().setActiveCustomFeatureMenu(paramName);
    ContextManager.getInstance().updateCustomFeatureValues({
      customFeature: {
        [page.name]: {
          [paramName]: {
            selectedFeature: selectedCustomFeature,
            originMenu: paramName,
            customFeatureType: `${SEGMENT_CUSTOM_FEATURE}|${FEATURE_SEGMENT}`,
            secondSegmentationConfig: secondSegmentationConfig,
          },
        },
      },
    });

    setCustomizationTray(FEATURE_CUSTOMIZATION);
  }

  setIsFilterTray(false);
  setCustomizationTrayTabId(FilterTrayTabId.SavedFilters);
};

/**
 * Function to update states when selecting custom class
 * @param preferenceId preference id
 * @param setIsActive function to set active preference
 * @param setCustomizationTray set customization tray
 */
export const updateWithCustomClass = (
  preferenceId: string,
  setIsActive: (preferenceId: string) => void,
  setCustomizationTray: React.Dispatch<React.SetStateAction<TrayType>>
) => {
  setIsActive(preferenceId);
  setCustomizationTray(null);
};

/**
 * Function to check if the selected menu item is a saved custom feature menu item
 * @param item selected menu item
 * @param currentValue current value
 * @returns true for saved custom feature menu item
 */
export const isSavedCustomFeatureMenuItem = (item: MenuItemOption, currentValue: string) => {
  const defaultCustomValue = `${SEGMENT_CUSTOM_FEATURE}|${FEATURE_SEGMENT}`;
  return (
    item.isCustom &&
    currentValue &&
    currentValue.includes(defaultCustomValue) &&
    typeof item.value === 'string' &&
    item.value.includes(defaultCustomValue) &&
    item.value !== currentValue
  );
};

/**
 * Function to update param changes when selecting data column menu item
 * @param dataColumns data column array
 * @param item selected item
 * @param newValues parameter to be updated
 */
export const handleSelectColumnItem = (dataColumns: MenuColumnOption[], item: ListBoxMenuItem, newValues: Params) => {
  const columnForItem = dataColumns.find(col => col.value === item.value);
  if (columnForItem) {
    newValues[UiDataParams.uiEnrichments] = columnForItem.enrichment ? [columnForItem.enrichment] : undefined;
  }
};
